import { useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DataRoomNav } from "@/components/DataRoomNav";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Clock, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TimeSlider from "@/components/charts/TimeSlider";
import ScrollTimeline from "@/components/ScrollTimeline";
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  Bar,
  Area,
  Brush,
  Cell,
} from "recharts";

const timelineData = [
  {
    period: "Q3 2021",
    title: "Marketplace Launch",
    description: "Initial platform launch focused on restaurant market entry due to COVID-19 impact",
    highlights: [
      "Launched marketplace targeting restaurants affected by pandemic",
      "COVID-19 created urgent need for digital procurement solutions",
      "Initial market validation in restaurant sector"
    ]
  },
  {
    period: "2021–2022",
    title: "Restaurants Focus & Dual Market Approach",
    description: "Concentrated efforts on restaurant market penetration, later expanding to hotels",
    highlights: [
      "Post-COVID recovery driving restaurant digitization",
      "Strong traction in restaurant procurement",
      "Strategic expansion into hotel market alongside restaurants"
    ]
  },
  {
    period: "Mid-2022",
    title: "VC Market Correction & Funding Winter",
    description: "Rising interest rates and tighter investment criteria reshape the startup ecosystem, driving focus on profitability over growth",
    highlights: [
      "Federal Reserve raises interest rates to combat inflation",
      "VC funding drops significantly across all sectors",
      "Shift from growth-at-all-costs to sustainable unit economics",
      "Market correction forces strategic reassessment"
    ]
  },
  {
    period: "Late 2022–Q1 2023",
    title: "Strategic Pivot & Market Consolidation",
    description: "Strategic pivot to focus on hotel industry while market competitors struggle",
    highlights: [
      "Fall of Sary (Middle East marketplace giant)",
      "Retailo App shuts down operations",
      "Multiple B2B marketplace consolidations",
      "Strategic shift back to hotels, shutdown restaurant operations",
      "Market correction creates opportunity for focused players"
    ]
  },
  {
    period: "2022–2023",
    title: "E-Tendering MVP, Global Supplier Access",
    description: "Development of e-tendering minimum viable product with global supplier network",
    highlights: [
      "Built comprehensive e-tendering platform",
      "Established global supplier partnerships",
      "Streamlined procurement processes for enterprise clients",
      "Focus on high-value, complex procurement needs"
    ]
  },
  {
    period: "2023–2024",
    title: "Strategic Product Focus & Business Model Pivot",
    description: "Critical business model reassessment leading to strategic product-focused approach",
    highlights: [
      "Misk Foundation and Plug & Play advised current model was unsustainable",
      "Identified need to shift to subscription model to become investable",
      "Q1 2024: Decision point between cash burning with thin margins vs product focus",
      "Strategic pivot from high operations intensity to solid product development",
      "Full-stack SaaS platform development with subscription focus"
    ]
  },
  {
    period: "2025",
    title: "Active Deals, Scaling Phase",
    description: "Current phase focused on growth and market expansion with proven platform",
    highlights: [
      "Multiple enterprise deals in pipeline",
      "Geographic expansion strategy",
      "Platform optimization based on user feedback",
      "Strategic partnerships with industry leaders",
      "Sustainable growth trajectory established"
    ]
  }
];

// Investment data processed by month
const investmentData = [
  { month: "2021-04", amount: 187500 },
  { month: "2021-07", amount: 93750 },
  { month: "2021-08", amount: 468750 }, // 93750 + 375000
  { month: "2021-09", amount: 62500 },
  { month: "2021-11", amount: 250000 }, // 93750 + 62500 + 93750
  { month: "2021-12", amount: 150000 },
  { month: "2022-01", amount: 150000 },
  { month: "2022-02", amount: 150000 },
  { month: "2022-03", amount: 337500 }, // 150000 + 100000 + 87500
  { month: "2022-04", amount: 162500 }, // 50000 + 50000 + 62500
  { month: "2022-05", amount: 800000 }, // 50000 + 750000
  { month: "2022-07", amount: 93750 },
  { month: "2022-10", amount: 375000 },
  { month: "2023-02", amount: 187500 },
  { month: "2023-05", amount: 217500 }, // 30000 + 187500
  { month: "2023-06", amount: 281250 },
  { month: "2023-08", amount: 375240 }, // 187620 + 187620
  { month: "2024-09", amount: 375000 }
];

// Monthly burn rate data
const burnRateData = [
  { month: "2020-12", amount: 7029.75 },
  { month: "2021-01", amount: 37827.65 },
  { month: "2021-02", amount: 12535.55 },
  { month: "2021-03", amount: 98268.86 },
  { month: "2021-04", amount: 54387.65 },
  { month: "2021-05", amount: 79498.8 },
  { month: "2021-06", amount: 85519.73 },
  { month: "2021-07", amount: 36987 },
  { month: "2021-08", amount: 121568.81 },
  { month: "2021-09", amount: 242062.04 },
  { month: "2021-10", amount: 535551.03 },
  { month: "2021-11", amount: 477820.22 },
  { month: "2021-12", amount: 495243.56 },
  { month: "2022-01", amount: 439061.87 },
  { month: "2022-02", amount: 347375.17 },
  { month: "2022-03", amount: 464998.85 },
  { month: "2022-04", amount: 365866.62 },
  { month: "2022-05", amount: 444237.28 },
  { month: "2022-06", amount: 175767.85 },
  { month: "2022-07", amount: 458664.58 },
  { month: "2022-08", amount: 92339.19 },
  { month: "2022-09", amount: 135543.88 },
  { month: "2022-10", amount: 352226.63 },
  { month: "2022-11", amount: 233743.88 },
  { month: "2022-12", amount: 217806.94 },
  { month: "2023-01", amount: 191354.53 },
  { month: "2023-02", amount: 205057.64 },
  { month: "2023-03", amount: 116821.39 },
  { month: "2023-04", amount: 210297.14 },
  { month: "2023-05", amount: 517427.51 },
  { month: "2023-06", amount: 278620.42 },
  { month: "2023-07", amount: 39901.82 },
  { month: "2023-08", amount: 427273.93 },
  { month: "2023-09", amount: 104674.03 },
  { month: "2023-10", amount: 299385.13 },
  { month: "2023-11", amount: 251254.93 },
  { month: "2023-12", amount: 54028.17 },
  { month: "2024-01", amount: 347501.08 },
  { month: "2024-02", amount: 241878.32 },
  { month: "2024-03", amount: 240775.8 },
  { month: "2024-04", amount: 380616.28 },
  { month: "2024-05", amount: 169795.14 },
  { month: "2024-06", amount: 279632.77 },
  { month: "2024-07", amount: 320885.67 },
  { month: "2024-08", amount: 14161.7 },
  { month: "2024-09", amount: 267320.71 },
  { month: "2024-10", amount: 114793.89 },
  { month: "2024-11", amount: 76560.22 },
  { month: "2024-12", amount: 291379.58 },
  { month: "2025-01", amount: 458.14 },
  { month: "2025-02", amount: 159468.1 },
  { month: "2025-03", amount: 197103.73 },
  { month: "2025-04", amount: 174306.37 },
  { month: "2025-05", amount: 229820.82 },
  { month: "2025-06", amount: 7598.75 },
  { month: "2025-07", amount: 60534.83 }
];

// Ahmed's investment data processed by month
const ahmedInvestmentData = [
  { month: "2020-12", amount: 100000 },
  { month: "2021-03", amount: 50000 },
  { month: "2021-10", amount: 30000 },
  { month: "2021-12", amount: 100000 }, // 30000 + 70000
  { month: "2022-01", amount: 50000 },
  { month: "2022-07", amount: 31000 },
  { month: "2022-08", amount: 41500 },
  { month: "2022-09", amount: 129000 }, // 100000 + 10000 + 19000
  { month: "2022-10", amount: 151000 }, // 120000 + 31000
  { month: "2022-12", amount: 21051 },
  { month: "2023-02", amount: 40000 },
  { month: "2023-04", amount: 189315 }, // 13146 + 110169 + 50000 + 16000
  { month: "2023-04", amount: 10000 },
  { month: "2023-05", amount: 40000 },
  { month: "2023-08", amount: 80000 }, // 40000 + 40000
  { month: "2023-10", amount: 140000 }, // 100000 + 40000
  { month: "2023-11", amount: 205000 }, // 140000 + 65000
  { month: "2023-11", amount: 140000 }, // 100000 + 40000
  { month: "2024-01", amount: 149000 },
  { month: "2024-01", amount: 148000 },
  { month: "2024-03", amount: 142000 },
  { month: "2024-04", amount: 328333 }, // 40000 + 8333.3 + 50000 + 190000 + 40000
  { month: "2024-05", amount: 258000 }, // 8000 + 250000
  { month: "2024-06", amount: 190000 },
  { month: "2024-07", amount: 261619 }, // 187604 + 74015.2
  { month: "2024-10", amount: 10000 }, // 1000 + 9000
  { month: "2024-11", amount: 79300 }, // 47000 + 10000 + 6800 + 5500 + 10000
  { month: "2024-12", amount: 303000 }, // 107000 + 46000 + 150000
  { month: "2025-02", amount: 160000 }, // 130000 + 30000
  { month: "2025-03", amount: 180000 }, // 70000 + 70000 + 40000
  { month: "2025-04", amount: 160000 }, // 120000 + 40000
  { month: "2025-05", amount: 130000 }, // 70000 + 60000
  { month: "2025-07", amount: 60000 }
];

// Monthly business revenue data (actual business income, NOT investments)
const revenueData = [
  { month: "2021-01", amount: 2500.0 },
  { month: "2021-02", amount: 10111.3 },
  { month: "2021-03", amount: 7643.85 }, // Removed investment portion (50000)
  { month: "2021-05", amount: 16500.0 },
  { month: "2021-06", amount: 12000.0 },
  { month: "2021-07", amount: 4700.0 }, // Removed investment portion (93750)
  { month: "2021-08", amount: 13441.36 }, // Removed investment portion (468750)
  { month: "2021-09", amount: 56285.66 }, // Removed investment portion (62500)
  { month: "2021-10", amount: 267594.62 }, // Removed Ahmed's investment (30000)
  { month: "2021-11", amount: 286480.08 }, // Removed investment portion (250000)
  { month: "2021-12", amount: 269050.87 }, // Removed investment and Ahmed portions (150000 + 100000)
  { month: "2022-01", amount: 240020.52 }, // Removed investment and Ahmed portions (150000 + 50000)
  { month: "2022-02", amount: 174349.18 }, // Removed investment portion (150000)
  { month: "2022-03", amount: 126574.47 }, // Removed investment portion (337500)
  { month: "2022-04", amount: 100334.39 }, // Removed investment portion (162500)
  { month: "2022-05", amount: 54600.09 }, // Removed investment portion (800000)
  { month: "2022-06", amount: 64177.4 },
  { month: "2022-07", amount: 58171.51 }, // Removed investment and Ahmed portions (93750 + 31000)
  { month: "2022-08", amount: 4140.63 }, // Removed Ahmed portion (41500)
  { month: "2022-09", amount: 1898.88 }, // Removed Ahmed portion (129000)
  { month: "2022-10", amount: 22093.13 }, // Removed investment and Ahmed portions (375000 + 151000)
  { month: "2022-11", amount: 387648.78 },
  { month: "2022-12", amount: 50019.39 }, // Removed Ahmed portion (21051)
  { month: "2023-01", amount: 32433.44 },
  { month: "2023-02", amount: 35117.59 }, // Removed investment and Ahmed portions (187500 + 40000)
  { month: "2023-03", amount: 15646.23 },
  { month: "2023-04", amount: 18961.86 }, // Removed Ahmed investment (199315)
  { month: "2023-05", amount: 283486.08 }, // Removed investment and Ahmed portions (217500 + 40000)
  { month: "2023-06", amount: 9845.0 }, // Removed investment portion (281250)
  { month: "2023-07", amount: 14165.2 },
  { month: "2023-08", amount: 25948.75 }, // Removed investment and Ahmed portions (375240 + 80000)
  { month: "2023-09", amount: 37471.73 },
  { month: "2023-10", amount: 174275.58 }, // Removed Ahmed portion (140000)
  { month: "2023-11", amount: -99217.59 }, // Removed Ahmed portion (345000), showing net outflow
  { month: "2023-12", amount: 55761.72 },
  { month: "2024-01", amount: 43667.55 }, // Removed Ahmed portion (297000)
  { month: "2024-02", amount: 270998.12 },
  { month: "2024-03", amount: 60369.14 }, // Removed Ahmed portion (142000)
  { month: "2024-04", amount: 60826.01 }, // Removed Ahmed portion (328333)
  { month: "2024-05", amount: 26525.97 }, // Removed Ahmed portion (258000)
  { month: "2024-06", amount: 39757.99 }, // Removed Ahmed portion (190000)
  { month: "2024-07", amount: 4844.38 }, // Removed Ahmed portion (261619)
  { month: "2024-08", amount: 647.0 },
  { month: "2024-10", amount: 0.0 }, // Removed Ahmed portion (10000)
  { month: "2024-11", amount: -10000.0 }, // Removed Ahmed portion (79300), showing net outflow
  { month: "2025-02", amount: 26769.6 }, // Removed Ahmed portion (160000)
  { month: "2025-03", amount: 46002.3 }, // Removed Ahmed portion (180000)
  { month: "2025-04", amount: 51980.0 }, // Removed Ahmed portion (160000)
  { month: "2025-05", amount: 0.0 }, // Removed Ahmed portion (130000)
  { month: "2025-06", amount: 1196.0 },
  { month: "2025-07", amount: 0.0 } // Removed Ahmed portion (60000)
];

// Combine all data into one comprehensive dataset
const createCombinedData = () => {
  const allMonths = new Set();
  
  // Collect all unique months
  [...investmentData, ...ahmedInvestmentData, ...burnRateData, ...revenueData].forEach(item => {
    allMonths.add(item.month);
  });
  
  // Create combined data array
  return Array.from(allMonths).sort().map(month => {
    const monthStr = String(month);
    const investment = investmentData.find(d => d.month === monthStr)?.amount || 0;
    const ahmedInvestment = ahmedInvestmentData.find(d => d.month === monthStr)?.amount || 0;
    const burnRate = burnRateData.find(d => d.month === monthStr)?.amount || 0;
    const revenue = revenueData.find(d => d.month === monthStr)?.amount || 0;
    
    // Calculate net cash flow (total inflow - outflow)
    const totalInflow = investment + ahmedInvestment + revenue;
    const netCashFlow = totalInflow - burnRate;
    
    return {
      month: monthStr,
      investment,
      ahmedInvestment,
      burnRate,
      revenue,
      totalInflow,
      netCashFlow
    };
  });
};

// Custom hook for scroll animations
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute('data-animate');
          if (id && entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observe all elements with data-animate attribute
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

const History = () => {
  const combinedData = createCombinedData();
  const visibleElements = useScrollAnimation();
  
  const [visibleSeries, setVisibleSeries] = useState({
    totalInflow: true,
    burnRate: true,
    netCashFlow: true,
    investment: false,
    ahmedInvestment: false,
    revenue: false
  });

  // Extract highlights from the bottom timeline to reuse in narrative callouts
  const getHighlights = (match: string) =>
    timelineData.find((t) => t.title.includes(match))?.highlights || [];

  const bulletsMarketplaceLaunch = getHighlights("Marketplace Launch");
  const bulletsVCMarketCorrection = getHighlights("VC Market Correction");
  const bulletsETendering = getHighlights("E-Tendering");
  const bulletsPivotSaaS = getHighlights("Strategic Product Focus");

  const [chartType, setChartType] = useState<'line' | 'area' | 'bar'>('line');
  const [brushRange, setBrushRange] = useState<{ startIndex: number; endIndex: number }>(() => ({
    startIndex: 0,
    endIndex: Math.max(0, combinedData.length - 1)
  }));

  // Handle time range selection - simplified
  const handleTimeRangeChange = useCallback((startIndex: number, endIndex: number) => {
    console.log('📊 Range changed:', { startIndex, endIndex });
    setBrushRange({ startIndex, endIndex });
  }, []);

  const toggleSeries = (seriesName: keyof typeof visibleSeries) => {
    setVisibleSeries(prev => ({
      ...prev,
      [seriesName]: !prev[seriesName]
    }));
  };

  const seriesConfig = [
    { key: 'totalInflow', label: 'Total Inflow', color: '#8b5cf6' },
    { key: 'burnRate', label: 'Total Outflow', color: '#ef4444' },
    { key: 'netCashFlow', label: 'Net Cash Flow', color: '#06b6d4' },
    { key: 'investment', label: 'External Investments', color: '#10b981' },
    { key: 'ahmedInvestment', label: "Founder's Funding", color: '#f59e0b' },
    { key: 'revenue', label: 'Business Revenue', color: '#3b82f6' }
  ];

  // Filter data based on brush range
  const filteredData = combinedData.slice(brushRange.startIndex, brushRange.endIndex + 1);

  // Animation helper function
  const getAnimationClass = (id: string, type: 'fade' | 'slide' | 'scale' = 'fade') => {
    const isVisible = visibleElements.has(id);
    if (!isVisible) return 'opacity-0 translate-y-10';
    
    switch (type) {
      case 'slide':
        return 'animate-slide-in-right opacity-100';
      case 'scale':
        return 'animate-scale-in opacity-100';
      default:
        return 'animate-fade-in opacity-100';
    }
  };

  const renderChart = () => {
    const chartProps = {
      data: filteredData,
      children: [
        <CartesianGrid key="grid" strokeDasharray="3 3" className="opacity-30" />,
        <XAxis 
          key="xaxis"
          dataKey="month" 
          tick={{ fontSize: 12 }}
          angle={-45}
          textAnchor="end"
          height={60}
        />,
        <YAxis 
          key="yaxis"
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
        />,
        <Tooltip 
          key="tooltip"
          formatter={(value, name) => [
            `SAR ${Number(value).toLocaleString()}`, 
            seriesConfig.find(s => s.key === name)?.label || name
          ]}
          labelStyle={{ color: 'hsl(var(--foreground))' }}
          contentStyle={{ 
            backgroundColor: 'hsl(var(--background))', 
            border: '1px solid hsl(var(--border))',
            borderRadius: '6px'
          }}
        />
      ]
    };

    const renderSeries = () => {
      return seriesConfig.map((series) => {
        if (!visibleSeries[series.key as keyof typeof visibleSeries]) return null;

        const commonProps = {
          key: series.key,
          dataKey: series.key,
          stroke: series.color,
          fill: series.color
        };

        if (chartType === 'line') {
          return (
            <Line 
              {...commonProps}
              type="monotone" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          );
        } else if (chartType === 'area') {
          return (
            <Area 
              {...commonProps}
              type="monotone" 
              strokeWidth={2}
              fillOpacity={0.1}
            />
          );
        } else {
          return (
            <Bar 
              {...commonProps}
              radius={[2, 2, 0, 0]}
            />
          );
        }
      });
    };

    if (chartType === 'line') {
      return <LineChart {...chartProps}>{chartProps.children}{renderSeries()}</LineChart>;
    } else if (chartType === 'area') {
      return <AreaChart {...chartProps}>{chartProps.children}{renderSeries()}</AreaChart>;
    } else {
      return <BarChart {...chartProps}>{chartProps.children}{renderSeries()}</BarChart>;
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DataRoomNav />
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto space-y-8">
            <div 
              className={`text-center mb-8 transition-all duration-700 ${getAnimationClass('header')}`}
              data-animate="header"
            >
              <h1 className="text-4xl font-bold text-primary mb-4">Company History & Timeline</h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Track our journey from inception to today, including key milestones, investments, and market developments that shaped our growth.
              </p>
            </div>

            {/* Combined Financial Overview Chart */}
            <Card 
              className={`mb-8 transition-all duration-700 ${getAnimationClass('chart', 'scale')}`}
              data-animate="chart"
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  Combined Financial Overview
                </CardTitle>
                <p className="text-muted-foreground">
                  Interactive multi-series timeline showing cash flow dynamics (amounts in SAR)
                </p>
                
                {/* Chart Controls */}
                <div className="flex flex-col lg:flex-row gap-4 mt-4">
                  {/* Chart Type Selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Chart Type:</label>
                    <Select value={chartType} onValueChange={(value: 'line' | 'area' | 'bar') => setChartType(value)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="line">Line</SelectItem>
                        <SelectItem value="area">Area</SelectItem>
                        <SelectItem value="bar">Bar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Series Toggle Controls */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 p-4 bg-muted/50 rounded-lg">
                  {seriesConfig.map((series) => (
                    <div key={series.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={series.key}
                        checked={visibleSeries[series.key as keyof typeof visibleSeries]}
                        onCheckedChange={() => toggleSeries(series.key as keyof typeof visibleSeries)}
                      />
                      <label
                        htmlFor={series.key}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                      >
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: series.color }}
                        />
                        {series.label}
                      </label>
                    </div>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-96 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    {renderChart()}
                  </ResponsiveContainer>
                </div>
                
                {/* Working Time Slider */}
                <div className="mt-6">
                  <TimeSlider
                    data={combinedData.map(item => ({
                      month: item.month,
                      netCashFlow: item.netCashFlow,
                      investment: item.investment,
                      ahmedInvestment: item.ahmedInvestment,
                      burnRate: item.burnRate,
                      revenue: item.revenue,
                      totalInflow: item.totalInflow
                    }))}
                    onRangeChange={handleTimeRangeChange}
                    height={120}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Interactive Timeline Infographic */}
            <div 
              className={`mb-8 transition-all duration-700 ${getAnimationClass('timeline-infographic')}`}
              data-animate="timeline-infographic"
            >
              <ScrollTimeline />
            </div>

            {/* The Story Behind the Numbers */}
            <Card 
              className={`mb-8 transition-all duration-700 ${getAnimationClass('story', 'fade')}`}
              data-animate="story"
            >
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-primary mb-2">
                  🧭 The Story Behind the Numbers
                </CardTitle>
                <p className="text-lg text-muted-foreground">
                  Context from the founder — what actually happened, and why it matters now.
                </p>
              </CardHeader>
              <CardContent>
                <div className="prose prose-lg max-w-none">
                  <div className="space-y-8 leading-relaxed text-foreground">
                    
                    {/* Opening Statement */}
                    <div className="bg-muted/30 p-6 rounded-lg border-l-4 border-primary">
                      <p className="text-lg text-foreground leading-8 mb-4">
                        Let's not talk theory. Let's talk about what actually happened.
                      </p>
                      <p className="text-muted-foreground leading-7">
                        Between twenty twenty-one and twenty twenty-five, Supplyfied lived through the full arc of what most startups never survive — and somehow, we're still here. What you'll see in the financial charts and the timeline isn't just a picture of cash in and out. It's a record of choices. Hard ones. And sometimes, the right ones too early.
                      </p>
                    </div>

                    {/* The Early Days */}
                    <div className="bg-card p-6 rounded-lg border shadow-sm">
                      <p className="text-muted-foreground leading-7 mb-4">
                        Supplyfied launched with a B2B marketplace model — an idea aligned with the times. The playbook looked solid: aggregate fragmented suppliers, connect them to hotels and restaurants, and scale. Like others, we expanded fast, even leaning on our restaurant network due to COVID's hit on tourism. But early on, we noticed something the market didn't want to admit yet: the unit economics were broken.
                      </p>
                      <p className="text-muted-foreground leading-7 mb-4">
                        Suppliers didn't behave like platform partners. They wanted upfront cash. Margins were thin. And our competitors — backed by multi-million dollar rounds — weren't succeeding because the model worked. They were succeeding because they were <strong className="text-foreground">fueling the supply chain with investor cash</strong>.
                      </p>
                      <p className="text-muted-foreground leading-7">
                        So yes, we stayed in the game longer than we should've. We were told, like many others, that this was the model VCs wanted. Until it wasn't.
                      </p>
                      <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
                        <h4 className="text-sm font-semibold text-foreground mb-2">Marketplace Launch</h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                          {bulletsMarketplaceLaunch.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Late 2022 Section */}
                    <div>
                      <h3 className="text-2xl font-bold text-primary mb-4 flex items-center gap-3">
                        🧭 Late 2022: The Moment of Truth
                      </h3>
                      <div className="bg-muted/20 p-6 rounded-lg border">
                        <p className="text-muted-foreground leading-7 mb-4">
                          By the end of twenty twenty-two, we'd spoken with over thirty investors. Nearly all said the same thing: "We're no longer backing B2B marketplaces. Show us SaaS." That was the spark. Internally, we started shifting. Externally, we brought on two co-founders — respected executives — to explore options. We gave the model one last test.
                        </p>
                        <p className="text-muted-foreground leading-7 mb-4">
                          It didn't pass.
                        </p>
                        <p className="text-muted-foreground leading-7">
                          And so, we did what most don't: <strong className="text-primary">we pivoted</strong>. Not because we failed — but because we saw what was coming before it hit.
                        </p>
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
                          <h4 className="text-sm font-semibold text-foreground mb-2">VC Market Correction</h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                            {bulletsVCMarketCorrection.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* What Came Next Section */}
                    <div>
                      <h3 className="text-2xl font-bold text-primary mb-4 flex items-center gap-3">
                        🧠 What Came Next
                      </h3>
                      <div className="bg-card p-6 rounded-lg border shadow-sm">
                        <p className="text-muted-foreground leading-7 mb-4">
                          In early twenty twenty-three, we began building our SaaS backbone — tools like SAC, AI-powered classification, and BoM automation. But funding didn't follow. While competitors burned tens of millions, we ran outbound campaigns without a marketing budget, hired without payroll security, and executed through sheer will. It was messy. It was exhausting. And it wasn't enough.
                        </p>
                        <p className="text-muted-foreground leading-7 mb-4">
                          Then came the Misk–Plug and Play program. Their feedback confirmed what we already knew: the model had to change. Not eventually — now. We shut down sales. Focused entirely on product. And started again.
                        </p>
                        <p className="text-muted-foreground leading-7">
                          By twenty twenty-four, the company was surviving on one thing: the founder's personal credit line. A loan carried us through the year, funding the product's completion and keeping the lights on while the market kept changing.
                        </p>
                        <div className="mt-4 p-4 bg-destructive/10 border-l-4 border-destructive rounded-r-lg">
                          <p className="text-foreground font-medium">We delivered. But at a cost.</p>
                        </div>
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
                          <h4 className="text-sm font-semibold text-foreground mb-2">E-Tendering MVP & Global Supplier Access</h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                            {bulletsETendering.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="mt-4 p-4 bg-muted/50 rounded-lg border">
                          <h4 className="text-sm font-semibold text-foreground mb-2">Pivot to SaaS & AI</h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                            {bulletsPivotSaaS.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Where Things Stand Section */}
                    <div>
                      <h3 className="text-2xl font-bold text-primary mb-4 flex items-center gap-3">
                        🔚 Where Things Stand
                      </h3>
                      <div className="bg-primary/5 p-6 rounded-lg border-l-4 border-primary">
                        <div className="space-y-3 mb-6">
                          <p className="text-foreground leading-7">Today, Supplyfied is co-sell aligned with Microsoft.</p>
                          <p className="text-foreground leading-7">Our SaaS tools are validated.</p>
                          <p className="text-foreground leading-7">The product is live.</p>
                          <p className="text-foreground leading-7">The market is real.</p>
                          <p className="text-foreground leading-7">The model is proven.</p>
                        </div>
                        <p className="text-muted-foreground leading-7 mb-4">
                          What we don't have is fuel. Not because we wasted it — but because we never had enough to waste in the first place.
                        </p>
                        <p className="text-muted-foreground leading-7">
                          And now, as VCs continue asking for traction before funding — we're facing the reality that without capital, even the right solution can stall.
                        </p>
                      </div>
                    </div>

                    {/* Closing Statement */}
                    <div className="bg-foreground/5 p-6 rounded-lg border">
                      <p className="text-muted-foreground leading-7 mb-4">
                        This page isn't written to justify anything. It's written to make it plain. We didn't just build a business. We built through everything the ecosystem, the capital markets, and the operating environment threw at us.
                      </p>
                      <p className="text-muted-foreground leading-7 mb-4">
                        We didn't quit. We didn't inflate. We didn't blame.
                      </p>
                      <p className="text-foreground leading-7 font-medium mb-2">
                        We did what we could, with what we had, for longer than most would've tried.
                      </p>
                      <p className="text-primary leading-7 font-semibold">
                        And now we're ready — not to restart, but to finally scale.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Startup Benchmarks Comparison */}
            <Card 
              className={`mb-8 transition-all duration-700 ${getAnimationClass('benchmarks', 'fade')}`}
              data-animate="benchmarks"
            >
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-primary mb-2">
                  📊 How Supplyfied Performs Against Startup Benchmarks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="grid gap-4 min-w-[800px] lg:min-w-0">
                    {/* Header Row */}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg border">
                      <div className="font-semibold text-foreground">Metric</div>
                      <div className="font-semibold text-foreground">Typical B2B Startups</div>
                      <div className="font-semibold text-foreground">Supplyfied</div>
                    </div>

                    {/* Benchmark Rows */}
                    {[
                      {
                        icon: "🔄",
                        metric: "Startup Survival",
                        typical: "~70% of startups fail within 2–5 years",
                        supplyfied: "Operating since 2021 — entering year five",
                        citation: "ff.co/startup-statistics-guide"
                      },
                      {
                        icon: "📈",
                        metric: "Marketplace Survivability", 
                        typical: "Over 80% of B2B marketplace models collapse before scaling",
                        supplyfied: "Pivoted to SaaS in Q4 2022, before collapse — ahead of the curve"
                      },
                      {
                        icon: "💰",
                        metric: "Capital Efficiency",
                        typical: "Raise US$5M–$50M+ for B2B marketplaces; Retailo and Sary raised over SAR 600M combined",
                        supplyfied: "Raised < SAR 2M total; founder personally funded SAR 1M+ in 2024 via loan"
                      },
                      {
                        icon: "🔁",
                        metric: "Pivot Timing",
                        typical: "Most startups pivot after failure or post-funding",
                        supplyfied: "Pivoted in Q4 2022, proactively — before internal collapse or outside pressure"
                      },
                      {
                        icon: "⚡",
                        metric: "Execution with Limited Capital",
                        typical: "Many stall without funding; product development slows",
                        supplyfied: "Built full AI suite and SaaS backend with limited capital, no structured marketing budget"
                      },
                      {
                        icon: "🧠",
                        metric: "Market Validation",
                        typical: "Few early startups engage with institutional or public sector entities",
                        supplyfied: "Strategic engagement with Ministry of Tourism, TDF, Plug & Play, and PwC"
                      },
                      {
                        icon: "🤝",
                        metric: "Strategic Partnerships",
                        typical: "Partnerships like Microsoft co-sell happen after Series A or large rounds",
                        supplyfied: "Achieved Microsoft Partner status, working toward co-sell readiness"
                      },
                      {
                        icon: "🎯",
                        metric: "Founder Skin in the Game",
                        typical: "Most founders stop investing after Seed; over 40% cite funding failure as the reason for closing down",
                        supplyfied: "Personally funded operations and development throughout 2024 — including via personal loan",
                        citation: "CB Insights"
                      },
                      {
                        icon: "🚀",
                        metric: "Product Readiness",
                        typical: "Still in MVP or early testing stages",
                        supplyfied: "Full SaaS platform built, AI tools functional, SAC engine validated"
                      }
                    ].map((row, index) => (
                      <div 
                        key={index}
                        className={`grid grid-cols-3 gap-4 p-4 rounded-lg border hover:shadow-md transition-all duration-300 ${
                          index % 2 === 0 ? 'bg-card' : 'bg-muted/10'
                        } hover:bg-muted/20 group`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                            {row.icon}
                          </span>
                          <div>
                            <div className="font-semibold text-foreground mb-1">{row.metric}</div>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground leading-relaxed">
                          {row.typical}
                          {row.citation && (
                            <div className="text-xs text-primary mt-1 opacity-70">
                              Source: {row.citation}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-foreground leading-relaxed font-medium">
                          {row.supplyfied}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary Footer */}
                <div className="mt-6 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg">
                  <h4 className="font-semibold text-primary mb-2">The Bottom Line</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    While most B2B startups struggle with survival, capital efficiency, and execution, Supplyfied has consistently outperformed typical benchmarks through strategic decision-making, capital discipline, and proactive adaptation to market conditions.
                  </p>
                </div>
              </CardContent>
            </Card>


            {/* Frequently Asked Questions */}
            <Card 
              className={`mb-8 transition-all duration-700 ${getAnimationClass('faq', 'fade')}`}
              data-animate="faq"
            >
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-primary mb-2">
                  ❓ Frequently Asked Questions
                </CardTitle>
                <p className="text-lg text-muted-foreground">
                  Common questions about our journey and strategic decisions.
                </p>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-left">
                      Are the financial figures on this page accurate?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        The figures represent the closest and most transparent snapshot based on internal data. We've already engaged an external team to audit and clean up all records. If they deliver as promised, we'll have fully verified financials within three to four weeks.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-left">
                      Why is Supplyfied's cash outflow so close to its inflow?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        During our marketplace phase, most of our spending went straight into COGS, because we didn't have the capital to pre-seed inventory like other players.
                      </p>
                      <p>
                        Startups like Sary and Retailo followed a Capital-Heavy Supply Seeding strategy — raising large rounds and flooding suppliers with upfront payments, sometimes in the six figures, to lock in supply and drive volume. We couldn't afford that. Our model was based on Demand-First execution, fulfilling orders on a Just-In-Time basis.
                      </p>
                      <p>
                        At the same time, Zid and Sala raised significant funding and made it easy for every supplier to launch their own e-commerce presence. By twenty twenty-two, suppliers no longer saw digital access as a differentiator — it had become a commodity.
                      </p>
                      <p>
                        These two forces — massive capital going to supply seeding, and the commoditization of supplier tech — reshaped the landscape. Our cash burn reflected the reality of operating lean in a market that was being reshaped by capital-heavy strategies and evolving supplier expectations.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-left">
                      Why didn't Supplyfied pivot from hotels to restaurants and hotels, then back to hotels, then to solutions instead of staying a marketplace?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        Supplyfied didn't jump aimlessly between sectors — it was iterating through models, not markets. Restaurants were tested and quickly dropped after deep losses. The focus returned to hotels because the fragmentation in procurement was worse, the spend larger, and the regulatory landscape more addressable. But the bigger problem wasn't hotels or restaurants — it was the limitations of the marketplace model itself. Suppliers resisted, margins were thin, and investor appetite shifted away from B2B e-commerce. Rather than force-fit the model, we extracted the infrastructure, data, and pain points into targeted SaaS products — including eProcurement, eTendering, SAC, and Review Management — creating what the market actually needed: automated tools, not another listing site.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger className="text-left">
                      Why did you pivot from B2B e-commerce to SaaS/AI?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        We didn't pivot on a whim — we responded to hard market data. By late 2022, B2B marketplaces were oversaturated and VCs had begun pulling back. At the same time, our internal tools (originally built to solve procurement/classification pain points) showed stronger retention, usage, and commercial viability. SaaS was the natural next step — leaner, more defensible, and more aligned with the region's evolving digital transformation goals.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger className="text-left">
                      What happened with the Ministry of Tourism?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        Our SAC (Smart Accommodation Classifier) caught the attention of both the Ministry and TDF. While early feedback was overwhelmingly positive, changes in leadership, internal silos, and resistance from legacy stakeholders delayed decision-making. Despite these challenges, we've maintained warm lines with the top and continue co-selling via Microsoft.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6">
                    <AccordionTrigger className="text-left">
                      Why not raise earlier? Why bootstrap this far?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        We did attempt multiple fundraising rounds. In each case, the feedback wasn't about the team or execution — it was about timing, business model preference, and B2B appetite. Instead of force-fitting into a hype narrative, we chose to build. That's why we have near-production-ready solutions today across multiple AI-driven verticals.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7">
                    <AccordionTrigger className="text-left">
                      What's live and what's in development?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        Several modules are ready or in production — including E-Procurement, SAC, Review Management, and our AI-powered Recipe Generator. Others are in final build or deploy-on-demand status. We only ship what's usable and revenue-ready, avoiding vaporware.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8">
                    <AccordionTrigger className="text-left">
                      What's different about Supplyfied vs other B2B/AI players?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      <p>
                        We operate from deep vertical insight. Most solutions in this space are horizontal or generic — ours are built with hospitality nuances in mind: OS&E standards, supplier compliance, star classification triggers, revenue/yield prediction, and regulatory integration. That's why we're taken seriously by both public and private sector players.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default History;